import {
  type Appearance,
  APPEARANCE_EVENTS,
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  effectiveAppearance,
  resolveAppearance,
  Toggle,
} from '@flashka/shared/ui';

import { MoonIcon } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import {
  type Context,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation } from 'react-router';

import { useLocalStorage } from '../components/hooks/use-local-storage';
import { hotData } from '../lib/hot-data';
import { stripLangPrefix } from '../seo/marketing-routes';

/**
 * Public routes always render in light mode regardless of theme preference:
 * the blog and the welcome pages, which still hardcode light hex CSS, in every
 * language. Everything else follows the theme — the app, and every `/home*`
 * URL (the v3 landing, its feature and section URLs, About), which ship their
 * own theme toggle; no older marketing subpage is left under `/home`. Keep in
 * sync with the pre-hydration script in `index.html`.
 */
export function isPublicRoute(pathname: string): boolean {
  const path = stripLangPrefix(pathname);
  return /^\/blog(\/|$)/.test(path) || /^\/welcome(-back)?(\/|$)/.test(path);
}

/** The user's preference — the shared {@link Appearance} (same on mobile). */
export type Theme = Appearance;

/** Resolved theme: always "light" or "dark". When selectedTheme is "system", follows OS. */
export type ResolvedTheme = 'light' | 'dark';

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

type ThemeContextValue =
  | {
      /** Resolved theme: what's currently active (light or dark). */
      theme: ResolvedTheme;
      /** User's preference: light, dark, or system. */
      selectedTheme: Theme;
      setTheme: (theme: Theme) => void;
    }
  | undefined;

/**
 * The SAME context object across hot reloads. This module exports plain
 * functions beside its components, so Fast Refresh re-runs all of it: a fresh
 * `createContext` left the mounted provider serving the old object while the
 * re-rendered toaster read the new one, and the dev page fell to the error
 * screen with "useTheme must be used within a ThemeProvider" (FLASHKA-VITE-1GE).
 */
const hot = hotData();
const ThemeContext: Context<ThemeContextValue> =
  (hot?.ThemeContext as Context<ThemeContextValue> | undefined) ??
  createContext<ThemeContextValue>(undefined);
if (hot) hot.ThemeContext = ThemeContext;

export function ThemeProvider({ children }: { children: ReactNode }) {
  // New users (no localStorage entry yet) get DEFAULT_APPEARANCE — the OS
  // while the shared FOLLOWS_SYSTEM switch is on. Pre-hydration script in
  // `index.html` applies the matching `.dark` class before React mounts to
  // avoid FOUC. `effectiveAppearance` maps a `system` an older build stored.
  const [rawTheme, setStoredTheme] = useLocalStorage<Theme>(
    APPEARANCE_STORAGE_KEY,
    DEFAULT_APPEARANCE,
  );
  const storedTheme = effectiveAppearance(rawTheme);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark());

  // Keep system preference in sync when theme is "system" (for ThemeSwitch / UI)
  useEffect(() => {
    if (storedTheme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPrefersDark(mql.matches);
    const handleChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [storedTheme]);

  const theme: ResolvedTheme = resolveAppearance(storedTheme, systemPrefersDark);

  return (
    <ThemeContext.Provider
      value={{ theme, selectedTheme: storedTheme, setTheme: setStoredTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Applies the `.dark` class to `<html>` based on the resolved theme AND
 * the current route. Public routes (landing, blog, welcome) stay light
 * regardless of preference. Must be rendered inside `<BrowserRouter>` so
 * it can read `useLocation`.
 *
 * Also keeps `useLocalStorage('theme')` in sync when the `.dark` class is
 * toggled externally (e.g., from Storybook).
 */
export function ThemeDOMSync() {
  const { theme, selectedTheme, setTheme } = useTheme();
  const location = useLocation();
  const isInternalChangeRef = useRef(false);

  // Apply / remove `.dark` whenever theme or route changes.
  useEffect(() => {
    const shouldBeDark = theme === 'dark' && !isPublicRoute(location.pathname);
    isInternalChangeRef.current = true;
    document.documentElement.classList.toggle('dark', shouldBeDark);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isInternalChangeRef.current = false;
      });
    });
  }, [theme, location.pathname]);

  // Storybook compatibility: when an external actor toggles the `.dark`
  // class on `<html>`, mirror it into the stored theme so the UI stays
  // consistent.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (isInternalChangeRef.current) return;
      const hasDarkClass = document.documentElement.classList.contains('dark');
      const newTheme: Theme = hasDarkClass ? 'dark' : 'light';
      if (newTheme !== selectedTheme && selectedTheme !== 'system') {
        setTheme(newTheme);
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, [selectedTheme, setTheme]);

  return null;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

function subscribeToDarkClass(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

const readDarkClass = (): ResolvedTheme =>
  document.documentElement.classList.contains('dark') ? 'dark' : 'light';

/**
 * The appearance actually PAINTED, read off the `.dark` class — which is not
 * the same as `useTheme().theme`. That one is the user's PREFERENCE, and
 * ThemeDOMSync deliberately ignores it on public routes (see isPublicRoute),
 * so a dark-preferring visitor on the landing page or the sign-in card gets a
 * light page while `theme` still says 'dark'.
 *
 * Anything that swaps an ASSET per theme (logos, illustrations) must follow
 * this, or it paints the dark variant onto a light background. Anything that
 * edits the preference (ThemeSwitch) wants `useTheme()` instead.
 *
 * Reading the class also makes these assets follow Storybook's theme toggle,
 * which writes the class directly.
 */
export function useAppliedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribeToDarkClass, readDarkClass, () => 'light');
}

/** Flips the preference between light and dark (Cmd/Ctrl+Shift+D). */
export function useToggleTheme() {
  const { setTheme, theme } = useTheme();
  const posthog = usePostHog();
  return useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    posthog.capture(APPEARANCE_EVENTS[next]!);
    setTheme(next);
  }, [theme, setTheme, posthog]);
}

export const ThemeSwitch = () => {
  const { setTheme, theme } = useTheme();
  const posthog = usePostHog();

  return (
    <div className="flex flex-row gap-3 rounded-[14px] border border-secondary-200 bg-secondary-50 p-1.5 pl-2">
      <MoonIcon className="h-icon-md w-icon-md" />
      <Toggle
        size="xs"
        checked={theme === 'dark'}
        onCheckedChange={(checked) => {
          const next = checked ? 'dark' : 'light';
          posthog.capture(APPEARANCE_EVENTS[next]!);
          setTheme(next);
        }}
      />
    </div>
  );
};
